/**
 * Wizard step persistence for the create/edit campaign flow.
 *
 * The wizard grew from 4 steps (Details+Schedule / Audience / Content / Review)
 * to 5 (Details / Audience / Content / Schedule / Review). A stored `4` is
 * therefore ambiguous, so persisted steps are versioned: values written by the
 * 5-step wizard carry `WIZARD_STEP_VERSION`, and only unversioned values are
 * migrated (legacy `4` was Review, which is now `5`).
 */

export const WIZARD_STEP_COUNT = 5;
export const WIZARD_STEP_VERSION = 2;

const WIZARD_STEP_STORAGE_PREFIX = `campaign-wizard-step:v${WIZARD_STEP_VERSION}:`;
const LEGACY_WIZARD_STEP_STORAGE_PREFIX = 'campaign-wizard-step:';

export type WizardLocationState = {
  wizardStep?: number;
  wizardStepVersion?: number;
};

export type CampaignPublishReadiness = {
  detailsValid: boolean;
  audienceValid: boolean;
  contentValid: boolean;
  scheduleValid: boolean;
};

export const getFirstInvalidCampaignStep = ({
  detailsValid,
  audienceValid,
  contentValid,
  scheduleValid,
}: CampaignPublishReadiness): number | null => {
  if (!detailsValid) return 1;
  if (!audienceValid) return 2;
  if (!contentValid) return 3;
  if (!scheduleValid) return 4;
  return null;
};

export const normalizeWizardStep = (value: unknown): number | null => {
  const step = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(step)) return null;
  if (step >= 1 && step <= WIZARD_STEP_COUNT) return step;
  return null;
};

export const migrateLegacyWizardStep = (step: number | null): number | null => {
  if (step === null) return null;
  // Legacy 4-step wizard: 4 was Review → 5. Steps 1–3 keep their meaning.
  if (step === 4) return WIZARD_STEP_COUNT;
  return step;
};

const getWizardStepStorageKey = (campaignId?: string) =>
  `${WIZARD_STEP_STORAGE_PREFIX}${campaignId?.trim() || 'new'}`;

const getLegacyWizardStepStorageKey = (campaignId?: string) =>
  `${LEGACY_WIZARD_STEP_STORAGE_PREFIX}${campaignId?.trim() || 'new'}`;

export const readWizardStepFromLocation = (state: unknown): number | null => {
  const wizardState = state as WizardLocationState | null;
  const step = normalizeWizardStep(wizardState?.wizardStep);
  if (step === null) return null;
  return wizardState?.wizardStepVersion === WIZARD_STEP_VERSION
    ? step
    : migrateLegacyWizardStep(step);
};

export const readWizardStepFromSession = (campaignId?: string): number | null => {
  try {
    const current = normalizeWizardStep(
      sessionStorage.getItem(getWizardStepStorageKey(campaignId))
    );
    if (current !== null) return current;

    const legacyKey = getLegacyWizardStepStorageKey(campaignId);
    const legacy = migrateLegacyWizardStep(normalizeWizardStep(sessionStorage.getItem(legacyKey)));
    if (legacy !== null) {
      sessionStorage.removeItem(legacyKey);
    }
    return legacy;
  } catch {
    return null;
  }
};

export const writeWizardStepToSession = (step: number, campaignId?: string) => {
  try {
    sessionStorage.setItem(getWizardStepStorageKey(campaignId), String(step));
  } catch {
    // Ignore storage quota / private-mode write failures.
  }
};

export const clearWizardStepFromSession = (campaignId?: string) => {
  try {
    sessionStorage.removeItem(getWizardStepStorageKey(campaignId));
    sessionStorage.removeItem(getLegacyWizardStepStorageKey(campaignId));
  } catch {
    // Ignore storage access failures.
  }
};

export const resolveInitialWizardStep = (locationState: unknown, campaignId?: string): number =>
  readWizardStepFromLocation(locationState) ?? readWizardStepFromSession(campaignId) ?? 1;
